/**
 * Jul-2026 (User-Wunsch) — Firmen-DNA-Hinweise im Arbeitskontext.
 * Zeigt die zum Bereich passenden Leitfäden (z. B. Telefonie, Probefahrt,
 * Lead-Bearbeitung) als einklappbare Karte. Rendert NICHTS, wenn der
 * Händler keine Inhalte gepflegt hat.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpenText, ChevronDown, ChevronUp, Paperclip, Sparkles } from "lucide-react";
import api from "../../lib/api";

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;

export const CultureHintCard = ({ area, compact = false }) => {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        api.get(`/dealer/culture/hints?area=${encodeURIComponent(area)}`)
            .then((r) => setData(r.data))
            .catch(() => {});
    }, [area]);

    if (!data || !(data.items || []).length) return null;

    return (
        <div className={`border border-amber-200 bg-amber-50/70 rounded-sm ${compact ? "mb-2" : "mb-4"}`} data-testid={`culture-hint-${area}`}>
            <button type="button" onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
                data-testid={`culture-hint-toggle-${area}`}>
                <BookOpenText className="w-4 h-4 text-amber-600 shrink-0" />
                <span className={`font-bold text-amber-900 ${compact ? "text-[11px]" : "text-xs"} uppercase tracking-wider`}>
                    Leitfaden: {data.label}
                </span>
                <span className="text-[10px] text-amber-600 font-bold">{data.items.length} Eintrag{data.items.length !== 1 ? "e" : ""}</span>
                <span className="ml-auto text-amber-600">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-2">
                    {data.items.map((e) => (
                        <div key={e.id} className="bg-white border border-amber-200 rounded-sm p-2.5" data-testid={`culture-hint-item-${e.id}`}>
                            <div className="text-[12px] font-bold text-gray-800">{e.title}</div>
                            {e.content && (
                                <p className={`text-[11px] text-gray-600 whitespace-pre-wrap mt-1 ${expanded === e.id ? "" : "line-clamp-4"}`}>{e.content}</p>
                            )}
                            {e.content && e.content.length > 300 && (
                                <button type="button" onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                                    className="text-[10px] font-bold text-amber-700 hover:underline mt-0.5"
                                    data-testid={`culture-hint-expand-${e.id}`}>
                                    {expanded === e.id ? "Weniger anzeigen" : "Mehr anzeigen"}
                                </button>
                            )}
                            {(e.attachments || []).length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                    {e.attachments.map((a, i) => (
                                        <a key={i} href={`${PUBLIC_BASE}${a.url}`} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-sm px-1.5 py-0.5 hover:bg-sky-100">
                                            <Paperclip className="w-2.5 h-2.5" /> {a.name}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] text-amber-700 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Diese Inhalte kennt auch der KI-Assistent.</span>
                        <Link to="/dealer/firmen-dna" className="text-[10px] font-bold text-amber-800 hover:underline" data-testid={`culture-hint-link-${area}`}>Firmen-DNA öffnen →</Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CultureHintCard;
